import EventEmitter from "eventemitter3";
import { EventTypes } from "../../../../../core/event-bus";
import { ProfileService } from "../../../../../services/profile-service";
import { Util } from "../../../../../utils/util";
import { FavoriteItemModal } from "../../../FavoriteItem/favorite-item-modal";
import { HalfModifierSelection } from "../../../../../types/enums";
import { DialogCreators } from "../../../../../utils/dialog-creators";
import { IUserFavoriteItem, IUserFavoriteItemOrderItem } from "../../../../../models/user.interface";
import { IOrderItem } from "../../../../../models/order.interface";
import { Order } from "../../../../../models/order";
import { Context } from "../../../../../models/context";
import { DataLayerService } from "../../../../../services/data-layer-service";
import { LocalizationService } from "../../../../../services/localization-service";
import { CustomizeItemCore } from "../../core/customize-item-core";

export interface ICustomizeItemFavoriteParams {
    eventBus: EventEmitter,
    order: Order,
    context: Context,
    profileService: ProfileService,
    localizationService: LocalizationService,
    dataLayerService: DataLayerService,
    core: CustomizeItemCore
}

export interface ICustomizeItemFavorite {
    isFavoriteItem: boolean,
    name: string
}

/**
 * Handles the favorite functionality for a customized item, including UI updates,
 * event subscriptions, and synchronization with user profile and order state.
 */
export class CustomizeItemFavorite {
    private readonly _eventBus: EventEmitter;
    private readonly _order: Order;
    private readonly _context: Context;
    private readonly _profileService: ProfileService;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _core: CustomizeItemCore;
    private readonly _buttons: (HTMLElement | null)[] = [];
    private _state: ICustomizeItemFavorite = { isFavoriteItem: false, name: "" };

    /**
     * @param {ICustomizeItemFavoriteParams} params - Initialization parameters for the favorite handler.
     */
    constructor(params: ICustomizeItemFavoriteParams) {
        this._eventBus = params.eventBus;
        this._order = params.order;
        this._context = params.context;
        this._localizationService = params.localizationService;
        this._profileService = params.profileService;
        this._dataLayerService = params.dataLayerService;
        this._core = params.core;

        this._buttons.push(
            document.getElementById("btn_customize_item_favorite") ?? null,
            document.getElementById("btn_customize_item_favorite_half") ?? null
        );

        this._registerEventBusListeners();
    }

    /**
     * Clears the click event and resets the favorite icon to default (non-favorite).
     */
    public clear(): void {
        for (const button of this._buttons) {
            if (button)
                button.onclick = null;

            this._toggleFavoriteIcon(button, false);
        }
    }

    /**
     * Attaches the click handler to the favorite button.
     * Used to toggle favorite status when clicked.
     */
    private _setEventListeners(button: HTMLElement | null): void {
        if (button)
            button.onclick = () => { this._onFavoriteButtonClick(button); };
    }

    /**
     * Registers listeners to handle favorite item updates via event bus.
     */
    private _registerEventBusListeners(): void {
        this._eventBus.on(EventTypes.FAV_ITEM_ADD, (name: string) => {
            this._setFavoriteItemState(true, name);

            for (const button of this._buttons) {
                this._toggleFavoriteIcon(button, true);
            }
        });
    }

    /**
     * Updates the internal state representing whether the item is favorited.
     * 
     * @param {boolean} state - Whether the item is marked as a favorite.
     * @param {string} name - The name of the item being favorited.
     */
    private _setFavoriteItemState(state: boolean, name: string): void {
        this._state = {
            isFavoriteItem: state,
            name: name,
        };
    }

    /**
     * Controls the visibility and ARIA label of the favorite button based on user login status.
     * Hides the button if user is not logged in, and resets favorite state.
     */
    public setFavoriteButtonRenderState(): void {
        for (const button of this._buttons) {
            if (!button)
                return;

            if (this._profileService.user.isLoggedIn()) {
                Util.showElement(button);
            } else {
                Util.hideElement(button);
                this._setFavoriteItemState(false, "");
            }

            button.setAttribute("aria-label", this._state.isFavoriteItem ? "Remove from Favorites" : "Add to Favorites");

            this._setEventListeners(button);
        }
    }

    /**
     * Attempts to build and save the current item as a favorite.
     * On success, emits a save event and resets favorite icon state.
     * On failure, shows an error dialog.
     *
     * @param {number} itemKey - The unique key identifying the item in the order.
     * @returns {Promise<void>}
     */
    public async favoriteItemProcessAsync(itemKey: number): Promise<void> {
        const result = await this._createAndSaveFavoriteItem(itemKey);

        if (result.success && result.favorite) {
            // Bug #56701: Skipping modal to avoid UI overlap with add-to-cart
            // DialogCreators.messageBoxOk(this._localizationService, this._localizationService.names("FavoriteItemSave_Success"));
            for (const button of this._buttons) {
                this._toggleFavoriteIcon(button, false);
            }

            this._eventBus.emit(EventTypes.FAV_ITEM_SAVE, result.favorite);
            this._setFavoriteItemState(false, "");
        } else if (!result.success) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayerService,
                this._localizationService.names("FavoriteItemSave_Error"),
                true
            );
        }
    }

    /**
     * Toggles the favorite button's icon classes.
     *
     * @param {boolean} isFavorite - If true, shows filled-heart icon; otherwise, default.
     */
    private readonly _toggleFavoriteIcon = (button: HTMLElement | null, isFavorite: boolean): void => {
        if (!button)
            return;

        button.classList.remove("icon-favorites", "icon-heart-filled");
        button.classList.add(isFavorite ? "icon-heart-filled" : "icon-favorites");
    }

    /**
     * Handles the click event on the favorite button.
     * - If adding a favorite, opens the modal to name it.
     * - If removing, resets the icon and internal state.
     */
    private readonly _onFavoriteButtonClick = (button: HTMLElement | null): void => {
        if (!button || !this._profileService.user.isLoggedIn())
            return;

        if (button.classList.contains("icon-favorites")) {
            // User wants to favorite the item
            const menuItem = this._core.getMenuItem(HalfModifierSelection.WHOLE);
            const dialog = new FavoriteItemModal(this._eventBus, this._context, this._dataLayerService, this._localizationService, menuItem);
            dialog.init();
            dialog.open();
        } else if (button.classList.contains("icon-heart-filled")) {
            // User wants to remove favorite
            this._toggleFavoriteIcon(button, false);
            this._setFavoriteItemState(false, "");
        }

        button.setAttribute("aria-label", this._state.isFavoriteItem ? "Remove from Favorites" : "Add to Favorites");
    }

    /**
     * Constructs a `IUserFavoriteItem` from the current order and saves it via the profile service.
     *
     * @param {number} itemKey - The item key of the newly added item to favorite.
     * @returns {Promise<{ success: boolean, favorite: IUserFavoriteItem | null }>} - Result of the save operation.
     */
    private async _createAndSaveFavoriteItem(itemKey: number): Promise<{ success: boolean, favorite: IUserFavoriteItem | null }> {
        if (!this._profileService.user.isLoggedIn() || !this._state.isFavoriteItem)
            return { success: true, favorite: null };

        const items = this._order.getProperty("Items");

        const addedItem = items.find(x => x.ItemKey === itemKey && x.HalfIndex < 2);
        if (!addedItem)
            return { success: true, favorite: null };

        const isHalfHalf = this._core.isHalfHalf();
        const addedItem2 = isHalfHalf
            ? items.find(x => x.ItemKey === itemKey && x.HalfIndex == 2)
            : undefined;

        // Maps an order item into the format required for a favorite item.
        const map = (orderItem: IOrderItem): IUserFavoriteItemOrderItem => ({
            itemId: orderItem.ItemId,
            index: orderItem.Index,
            sizeId: orderItem.SizeId,
            quantity: orderItem.Quantity,
            modifiers: orderItem.Modifiers.map(mod => ({
                modifierId: mod.ModifierId,
                index: mod.Index,
                modifierDisplayGroupIndex: mod.ModifierDisplayGroupIndex,
                iIndex: mod.iIndex,
                iModifierDisplayGroupIndex: mod.iModifierDisplayGroupIndex,
                preModifierId: mod.PreModifierId,
                isDefault: mod.IsDefault,
                modifierDisplayGroupId: mod.ModifierDisplayGroupId,
                mergedModifierDisplayGroupId: mod.MergedModifierDisplayGroupId,
                countForPricing: mod.CountForPricing,
                countForPricingByItself: mod.CountForPricingByItself,
            })),
            price: orderItem.Price,
            itemWebCategoryId: orderItem.ItemWebCategoryId,
            itemCategoryId: orderItem.ItemCategoryId,
            itemKey: orderItem.ItemKey,
            itemRecId: orderItem.ItemRecId,
            halfCount: orderItem.HalfCount,
            halfIndex: orderItem.HalfIndex,
            comment: orderItem.Comment,
            isBuildYourOwn: orderItem.IsBuildYourOwn,
            instructions: orderItem.Instructions ? [...orderItem.Instructions] : undefined,
            names: [],
            imageUrl: ''
        });

        const favorite: IUserFavoriteItem = {
            favoriteItemId: 0,
            favoriteItemName: this._state.name,
            isHalfHalf,
            items: [map(addedItem), ...(addedItem2 ? [map(addedItem2)] : [])],
            favoriteDateUTC: new Date().toISOString()
        };

        const savedId = await this._profileService.saveFavoriteItem(favorite);
        if (savedId > 0) {
            favorite.favoriteItemId = savedId;
            return { success: true, favorite };
        }

        return { success: false, favorite: null };
    }
}